import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Plus, Trash2, Info } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "../lib/format";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../components/etams/PalangDaftar";
import { apiErrorMessage } from "../lib/inventoryApi";
import Hint from "../components/etams/Hint";

const KOLOM_CSV = [
  { kunci: "holiday_date", judul: "Tanggal" },
  { kunci: "name", judul: "Nama" },
  { kunci: "scope", judul: "Cakupan", ambil: (h) => (h.scope === "NATIONAL" ? "Nasional" : "Regional") },
  { kunci: "region_id", judul: "Wilayah" },
  { kunci: "notes", judul: "Catatan" },
];

/**
 * Holiday Calendar — C3b-β rewrite.
 *
 * Backend (C2b, unchanged):
 *   GET    /api/holidays?year=&region_id=
 *   POST   /api/holidays   { holiday_date, name, scope: "NATIONAL"|"REGIONAL", region_id?, notes? }
 *   DELETE /api/holidays/{id}
 *
 * Access (matches backend behavior):
 *   - Owner: full CRUD on NATIONAL + REGIONAL (any region)
 *   - Kepala Cabang: REGIONAL only for own region; cannot touch NATIONAL
 *
 * Edit is intentionally not implemented (backend has no PATCH endpoint per C2b).
 * To change a holiday: delete + recreate (workflow documented inline).
 */
export default function Holidays() {
  const { isOwner, isKepalaCabang, profile } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const [regionFilter, setRegionFilter] = useState("ALL");
  const [items, setItems] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const myRegionId = profile?.region_id || null;
  const canCreate = isOwner || isKepalaCabang;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { year };
      // Kacab is automatically scoped backend-side (GET returns all + region_id filter optional).
      if (isKepalaCabang && myRegionId) params.region_id = myRegionId;
      else if (isOwner && regionFilter !== "ALL") params.region_id = regionFilter;
      const [hr, rg] = await Promise.all([
        api.get("/holidays", { params }),
        isOwner ? api.get("/regions") : Promise.resolve({ data: [] }),
      ]);
      setItems(hr.data.items || hr.data || []);
      setRegions(rg.data || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat hari libur"));
    } finally { setLoading(false); }
  }, [year, regionFilter, isKepalaCabang, isOwner, myRegionId]);

  useEffect(() => { load(); }, [load]);
  // Filter tahun/region tetap ke server; cari/urut/paginasi di klien (Tahap 2+4 ERP).
  const daftar = useDaftar(items, {
    kunciCari: ["name", "notes"],
    urutAwal: { kunci: "holiday_date", arah: "asc" },
  });

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/holidays/${confirmDelete.id}`);
      toast.success("Hari libur dihapus");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Hari libur gagal dihapus"));
    } finally { setConfirmDelete(null); }
  };

  const years = [];
  const cur = new Date().getFullYear();
  for (let y = cur - 1; y <= cur + 2; y++) years.push(y);

  const regionName = (id) => regions.find((r) => r.id === id)?.name || "—";
  const canDeleteRow = (h) => {
    if (isOwner) return true;
    if (isKepalaCabang) return h.scope === "REGIONAL" && h.region_id === myRegionId;
    return false;
  };

  return (
    <div className="space-y-6" data-testid="holidays-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            Hari Libur{" "}
            <Hint>Tanggal libur Nasional dan Regional dipakai untuk menghitung deadline FA.{isKepalaCabang && " Kepala Cabang hanya bisa mengelola libur Regional untuk cabangnya."}</Hint>
          </h1>
        </div>
        {canCreate && (
          <Button onClick={() => setShowForm(true)} data-testid="holiday-new-btn">
            <Plus size={16} className="mr-1" /> Tambah Hari Libur
          </Button>
        )}
      </div>

      <Card className="p-4">
        <PalangDaftar daftar={daftar} placeholder="Cari nama hari libur…" testId="holiday-palang"
                      ekspor={{ nama: `hari_libur_${year}`, kolom: KOLOM_CSV }}>
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-32 h-9" data-testid="holiday-year-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          {isOwner && regions.length > 0 && (
            <Select value={regionFilter} onValueChange={setRegionFilter}>
              <SelectTrigger className="w-56 h-9" data-testid="holiday-region-filter"><SelectValue placeholder="Wilayah" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Semua Wilayah (Nasional + Regional)</SelectItem>
                {regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </PalangDaftar>

        <div className="mb-4 px-3 py-2 rounded-md bg-info-soft border border-info-soft-border flex items-start gap-2 text-xs text-info-soft-foreground" data-testid="holiday-edit-note">
          <Info size={14} className="mt-0.5 shrink-0" />
          <div>Untuk mengubah hari libur: <strong>hapus lalu tambah ulang</strong>.</div>
        </div>

        {loading ? <div className="py-8 text-center text-sm text-muted-foreground" data-testid="holidays-loading">Memuat...</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                <tr>
                  <ThUrut daftar={daftar} kunci="holiday_date" className="text-left px-3 py-2">Tanggal</ThUrut>
                  <ThUrut daftar={daftar} kunci="name" className="text-left px-3 py-2">Nama</ThUrut>
                  <ThUrut daftar={daftar} kunci="scope" className="text-left px-3 py-2">Cakupan</ThUrut>
                  <th className="text-left px-3 py-2">Wilayah</th>
                  <th className="text-left px-3 py-2">Catatan</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {daftar.total === 0 && (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                    {daftar.cari.trim() ? "Tidak ada yang cocok." : `Tidak ada hari libur di tahun ${year}.`}
                  </td></tr>
                )}
                {daftar.tampil.map((h) => (
                  <tr key={h.id} className="border-t border-border" data-testid={`holiday-row-${h.id}`}>
                    <td className="px-3 py-2 font-medium">{formatDate(h.holiday_date)}</td>
                    <td className="px-3 py-2">{h.name}</td>
                    <td className="px-3 py-2">{h.scope === "NATIONAL" ? <Badge className="bg-neutral-soft text-neutral-soft-foreground hover:bg-neutral-soft">Nasional</Badge> : <Badge variant="outline">Regional</Badge>}</td>
                    <td className="px-3 py-2 text-xs">{h.scope === "REGIONAL" ? regionName(h.region_id) : <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{h.notes || "—"}</td>
                    <td className="px-3 py-2 text-right">
                      {canDeleteRow(h) ? (
                        <Button variant="ghost" size="icon" onClick={() => setConfirmDelete(h)} data-testid={`holiday-delete-${h.id}`} className="text-danger">
                          <Trash2 size={14} />
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground" data-testid={`holiday-readonly-${h.id}`}>hanya baca</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Paginasi daftar={daftar} testId="holiday-paginasi" />
          </div>
        )}
      </Card>

      {showForm && (
        <HolidayFormModal
          regions={regions}
          isOwner={isOwner}
          isKepalaCabang={isKepalaCabang}
          myRegionId={myRegionId}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="confirm-delete-holiday">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Hari Libur?</AlertDialogTitle>
            <AlertDialogDescription>
              "{confirmDelete?.name}" pada {formatDate(confirmDelete?.holiday_date)} akan dihapus. Deadline FA yang sudah dihitung tidak otomatis dikoreksi.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-danger text-danger-foreground hover:bg-danger/90" data-testid="holiday-delete-confirm">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function HolidayFormModal({ regions, isOwner, isKepalaCabang, myRegionId, onClose, onSaved }) {
  // KC defaults to REGIONAL with auto-locked region; Owner defaults to NATIONAL.
  const [form, setForm] = useState({
    holiday_date: "",
    name: "",
    scope: isKepalaCabang ? "REGIONAL" : "NATIONAL",
    region_id: isKepalaCabang ? (myRegionId || "") : "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.holiday_date || !form.name.trim()) { toast.error("Tanggal dan nama wajib diisi"); return; }
    if (form.scope === "REGIONAL" && !form.region_id) { toast.error("Wilayah wajib dipilih untuk libur Regional"); return; }
    setSaving(true);
    try {
      const body = {
        holiday_date: form.holiday_date,
        name: form.name.trim(),
        scope: form.scope,
        region_id: form.scope === "REGIONAL" ? form.region_id : null,
        notes: form.notes || null,
      };
      await api.post("/holidays", body);
      toast.success("Hari libur ditambahkan");
      onSaved();
    } catch (e) {
      const err = e?.response?.data?.detail?.error;
      const msgMap = {
        OWNER_ONLY_NATIONAL: "Hanya Owner yang bisa menambah libur Nasional. Kalau perlu, hubungi Owner.",
        REGIONAL_REQUIRES_REGION_ID: "Wilayah wajib diisi untuk libur Regional.",
        NATIONAL_MUST_NOT_HAVE_REGION: "Libur Nasional tidak boleh punya cabang.",
        REGION_SCOPE: "Kamu hanya bisa menambah libur untuk cabangmu.",
      };
      toast.error(msgMap[err] || apiErrorMessage(e, "Hari libur gagal disimpan"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="holiday-form-modal">
        <DialogHeader><DialogTitle>Tambah Hari Libur</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Tanggal *</Label>
            <Input type="date" value={form.holiday_date} onChange={(e) => upd("holiday_date", e.target.value)} data-testid="holiday-form-date" />
          </div>
          <div>
            <Label>Nama *</Label>
            <Input value={form.name} onChange={(e) => upd("name", e.target.value)} placeholder="cth: Hari Kemerdekaan RI" data-testid="holiday-form-name" />
          </div>
          <div>
            <Label>Cakupan *</Label>
            <Select value={form.scope} onValueChange={(v) => upd("scope", v)} disabled={isKepalaCabang}>
              <SelectTrigger data-testid="holiday-form-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                {isOwner && <SelectItem value="NATIONAL">Nasional (berlaku semua cabang)</SelectItem>}
                <SelectItem value="REGIONAL">Regional (1 cabang saja)</SelectItem>
              </SelectContent>
            </Select>
            {isKepalaCabang && <div className="text-xs text-muted-foreground mt-1">Sebagai Kepala Cabang, hanya bisa tambah libur Regional.</div>}
          </div>
          {form.scope === "REGIONAL" && (
            <div>
              <Label>Wilayah *</Label>
              <Select value={form.region_id} onValueChange={(v) => upd("region_id", v)} disabled={isKepalaCabang}>
                <SelectTrigger data-testid="holiday-form-region"><SelectValue placeholder="Pilih cabang" /></SelectTrigger>
                <SelectContent>{regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
              {isKepalaCabang && <div className="text-xs text-muted-foreground mt-1">Wilayah terkunci ke wilayah Anda.</div>}
            </div>
          )}
          <div>
            <Label>Catatan</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => upd("notes", e.target.value)} data-testid="holiday-form-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={saving} data-testid="holiday-form-save">{saving ? "Menyimpan..." : "Simpan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
